/**
 * Utility functions for WorkspaceHeaderLayout component
 * 
 * This file contains reusable utility functions that are used across
 * the workspace header components to reduce code duplication and improve maintainability.
 */

import { UserSearchResult } from '@/apis/core/user/get/searchUsers';

// ============= Types =============

/**
 * Represents a member that is pending addition to a workspace
 */
export interface PendingMember {
  id: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  email?: string;
  isOwner: boolean;
}

// ============= Constants =============

/**
 * Minimum number of characters required for user search
 */
export const MIN_SEARCH_LENGTH = 3;

/**
 * Debounce delay for user search (in milliseconds)
 */
export const SEARCH_DEBOUNCE_DELAY = 300;

// ============= Utilities =============

/**
 * Formats a user search result into a display string
 * 
 * @param suggestion - The user search result to format
 * @returns Formatted string in the format "FirstName LastName (email)" or fallback to id
 * 
 * @example
 * formatUserSearchResult({ firstName: 'John', lastName: 'Doe', username: 'john@example.com' })
 * // Returns: "John Doe (john@example.com)"
 */
export const formatUserSearchResult = (suggestion: UserSearchResult): string => {
  const fullName = `${suggestion.firstName || ''} ${suggestion.lastName || ''}`.trim();
  const email = suggestion.username || '';
  return email ? `${fullName} (${email})` : fullName || suggestion.id;
};

/**
 * Gets the full name of a pending member
 * 
 * @param member - The pending member object
 * @returns Full name string, or username, or id as fallback
 * 
 * @example
 * getFullName({ firstName: 'John', lastName: 'Doe', username: 'john@example.com' })
 * // Returns: "John Doe"
 */
export const getFullName = (member: PendingMember): string => {
  const firstName = member.firstName || '';
  const lastName = member.lastName || '';
  return `${firstName} ${lastName}`.trim() || member.username || member.id;
};

/**
 * Extracts error response data from an error object
 * 
 * This utility helps standardize error handling by extracting response data
 * that may be attached to error objects in different formats.
 * 
 * @param err - The error object (can be Error, ApiError, or any)
 * @returns Response data if available, undefined otherwise
 */
export const extractErrorResponse = (err: unknown): unknown => {
  return (err as any)?.response || (err as any)?.body;
};

/**
 * Creates a standardized error object from various error types
 * 
 * @param err - The error to normalize
 * @param defaultMessage - Default message if error is not an Error instance
 * @returns Error instance
 */
export const normalizeError = (err: unknown, defaultMessage: string): Error => {
  return err instanceof Error ? err : new Error(defaultMessage);
};

/**
 * Resets invite modal state to initial values
 * 
 * @returns Object containing reset state values
 */
export const getResetInviteModalState = () => ({
  pendingMembers: [] as PendingMember[],
  memberInput: '',
  options: [] as string[],
  lastSuggestions: [] as UserSearchResult[],
  isOpen: false,
});
